<template>
	<div>
		<message-details-overlay
			:fixed="true"
			:message="selectedMessage"
			:user="user"
			:serviceIcons="serviceIcons"
			@close="selectedMessage = null"
			@select="highlightMessage($event)"
		/>
		<div class="d-flex align-items-center mb-3">
			<b-breadcrumb class="flex-grow-1 mb-0">
				<b-breadcrumb-item to="/">{{$title}}</b-breadcrumb-item>
				<b-breadcrumb-item active>URL harvester</b-breadcrumb-item>
			</b-breadcrumb>
			<b-button :disabled="loading > 0 || urls?.length <= 0" variant="danger" class="ml-2" style="padding: .70rem" @click.prevent="confirmClearUrls()">Clear</b-button>
		</div>
		<b-alert show v-if="error" variant="danger">{{error&&(error.error||error.toString())}}</b-alert>
		<div class="text-center" v-else-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="4"></b-icon>
		</div>
		<div v-else>
			<b-list-group class="p-0 list-group-striped" v-if="urls?.length > 0">
				<b-list-group-item v-for="(item, idx) in urls" :key="idx">
					<b-form-row>
						<div class="col-lg">
							<media-item :item="item">
								<template #dropdown>
									<b-dropdown-item @click="confirmDeleteUrl(item)" variant="danger"><b-icon-trash/> Remove</b-dropdown-item>
								</template>
							</media-item>
						</div>
						<div class="col-lg">
							<show-more height="90">
								<chat-message :item="message" :serviceIcons="serviceIcons" :user="user" :giftTypes="giftTypes" v-for="(message, midx) in item.messages" :key="midx" @select="selectedMessage = $event" @menuclick="highlightMessage($event)"/>
							</show-more>
						</div>
					</b-form-row>
				</b-list-group-item>
			</b-list-group>
			<b-card v-else class="text-center text-muted p-5">No URLs have been harvested yet.<br />As people send links in your chats, they will appear here.</b-card>
		</div>
	</div>
</template>

<script>
import util from '../util';
import MediaItem from '../components/MediaItem.vue';
import ChatMessage from '../components/ChatMessage.vue';
import ShowMore from '../components/ShowMore.vue';
import MessageDetailsOverlay from '../components/MessageDetailsOverlay.vue';

export default {
	components: {
		ChatMessage,
		ShowMore,
		MediaItem,
		MessageDetailsOverlay,
	},
	data() { return {
		loading: 0,
		error: null,
		urls: [],
		services: [],
		serviceIcons: {},
		giftTypes: {},
		user: null,
		selectedMessage: null,
	}},
	filters: {
		secondsToTime: util.secondsToTime,
		formatSQLDate: function(d) {
			let date = util.sqlStringToDate(d);
			return date ? date.toLocaleString() : '-';
		},
		money: util.moneyString
	},
	async mounted() {
		try {
			this.loading++;
			this.user = await this.$api.get('userData');
			await this.loadUrls();
			this.services = await this.$api.get('services');
			this.serviceIcons = Object.fromEntries(this.services.filter(s => s.icon?.url).map(s => [ s.id, s.icon.url ]));
			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				urls: () => this.loadUrls(),
			});
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		this.subId&&this.$socket.unsubscribe(this.subId);
	},
	methods: {
		highlightMessage(msg) {
			const target = document.getElementById(msg.id);
			target?.classList.add('loading');
			const is_current = String(msg.serviceId) == String(this.user?.highlighted_message?.serviceId) && msg.token == this.user?.highlighted_message?.token;
			this.$api.post('selectMessage', is_current ? null : msg).then(() => {
				document.querySelectorAll('#app .message.highlighted').forEach(el => el.classList.remove('highlighted'));
				if(is_current) {
					this.user.highlighted_message = null;
				} else {
					target?.classList.add('highlighted');
					this.user.highlighted_message = msg;
				}
			}).catch(err => {
				this.$bvToast.toast('Could not select message: ' + (err.error || err.toString()), {
					title: 'Error', variant: 'danger', solid: true,
				});
			}).finally(() => {
				target?.classList.remove('loading');
			});
			this.$broadcastChannel?.postMessage({
				type: 'highlightedMessage',
				data: is_current ? null : JSON.parse(JSON.stringify(msg)),
			});
		},
		async loadUrls() {
			try {
				this.loading++;
				const urls = await this.$api.get('urls');
				this.urls = urls.map(u => ({ ...u, added_at: u.info?.posted_at, updated_at: u.info?.updated_at }));
			} catch(e) {
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		toggleAllChats(item) {
			this.$set(item, 'showAllChats', !item.showAllChats);
		},
		confirmDeleteUrl(item) {
			this.$bvModal.msgBoxConfirm(`Are you sure you want to delete ${item.info.url}?`)
			.then(value => {
				if(!value) return;

				this.$api.delete('url', { url: item.info.url }).then(() => {
					this.$bvToast.toast('Successfully deleted', {
						title: 'Success',
						variant: 'success',
						solid: true
					});
				}).catch(err => {
					this.$bvToast.toast('Could not delete: '+(err.error||err.toString()), {
						title: 'Error',
						variant: 'danger',
						solid: true
					});
				}).finally(() => {
					this.loadUrls();
				});
			})
			.catch(err => {
				console.error(`Error confirming deletion for ${item.url}`, err);
			});
		},
		async confirmDeleteChat(chat, item, idx) {
			try {
				const value = await this.$bvModal.msgBoxConfirm(`Are you sure you want to delete ${item.info.url}?`);
				if(!value) throw 'Denied';
			} catch(e) {
				console.error('Confirming deletion', e);
				return;
			}

			try {
				await this.$api.delete('messageUrl', { serviceId: chat.serviceId, messageToken: chat.messageToken, url: item.info.url});
				item.chats.splice(idx, 1);
				this.$bvToast.toast('Deleted message URL', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
			} catch(e) {
				this.$bvToast.toast('Could not delete message URL: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true
				});
			}
		},
		async confirmClearUrls() {
			try {
				const value = await this.$bvModal.msgBoxConfirm(`Are you sure you want to delete all harvested URLs?`);
				if(!value) throw 'Denied';
			} catch(e) {
				console.error('Confirming deletion', e);
				return;
			}

			try {
				await this.$api.delete('urls');
				this.$bvToast.toast('Cleared URLs', {
					title: 'Success',
					variant: 'success',
					solid: true,
				});
			} catch(e) {
				this.$bvToast.toast('Could not delete URLs: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				this.loadUrls();
			}
		},
	}
}
</script>
